'use client';

import { getPathWithoutLocale } from 'helpers/hooks/usePath';

/** BAD-2715 exposure metadata for same-tab application start attribution. */
export type AbTestExposureContext = {
  experimentId: string;
  experimentName: string;
  variantAssignment: string;
  shownVariantEntryTitle: string;
  timestamp: string;
  userSessionIdentifier: string;
};

/** GTM listens for this event name to trigger data collection. */
const GTM_EVENT = 'eventTracker';
/** Always used for Contentful / Ninetailed A/B pushes. */
const GTM_EVENT_CATEGORY = 'Contentful';

/** In-memory session state for the SPA tab (no sessionStorage). */
let inMemorySessionId: string | null = null;
let inMemoryLastExposure: AbTestExposureContext | null = null;

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const values = new Uint32Array(4);
    crypto.getRandomValues(values);
    const token = Array.from(values, (value) => value.toString(16).padStart(8, '0')).join('');
    return `session-${Date.now()}-${token}`;
  }

  return `session-${Date.now()}-fallback`;
};

/** @internal test helper */
export const resetAbTestAnalyticsSessionForTests = (): void => {
  inMemorySessionId = null;
  inMemoryLastExposure = null;
};

export const formatVariantAssignment = (variantIndex: number | undefined): string => {
  if (variantIndex === undefined || variantIndex === null) return 'unknown';
  if (variantIndex === 0) return 'control';
  return `variant ${variantIndex}`;
};

export const getUserSessionIdentifier = (providedSessionIdentifier?: string): string => {
  if (typeof window === 'undefined') return '';

  if (inMemorySessionId) return inMemorySessionId;

  const sessionId = providedSessionIdentifier || createSessionId();
  inMemorySessionId = sessionId;

  return sessionId;
};

const getDefaultSourcePageSurface = (): string => {
  if (typeof window === 'undefined') return '';
  return getPathWithoutLocale(window.location.pathname);
};

const rememberExposureForApplicationStart = (context: AbTestExposureContext): void => {
  inMemoryLastExposure = context;
};

export const getStoredAbTestExposure = (): AbTestExposureContext | null => inMemoryLastExposure;

const pushToDataLayer = (payload: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

export type TrackAbTestExposureInput = {
  experimentId: string;
  experimentName: string;
  variantIndex: number | undefined;
  shownVariantEntryTitle: string;
  sourcePageSurface?: string;
  userSessionIdentifier?: string;
};

/** BAD-2715: fires when A/B-tested content is rendered to the customer. */
export const trackAbTestExposure = ({
  experimentId,
  experimentName,
  variantIndex,
  shownVariantEntryTitle,
  sourcePageSurface,
  userSessionIdentifier: providedSessionIdentifier,
}: TrackAbTestExposureInput): AbTestExposureContext => {
  const timestamp = new Date().toISOString();
  const userSessionIdentifier = getUserSessionIdentifier(providedSessionIdentifier);
  const variantAssignment = formatVariantAssignment(variantIndex);
  const pageSurface = sourcePageSurface ?? getDefaultSourcePageSurface();

  const exposure: AbTestExposureContext = {
    experimentId,
    experimentName,
    variantAssignment,
    shownVariantEntryTitle,
    timestamp,
    userSessionIdentifier,
  };

  rememberExposureForApplicationStart(exposure);

  pushToDataLayer({
    event: GTM_EVENT,
    eventCategory: GTM_EVENT_CATEGORY,
    // GTM convention: action = experience name, label = variant assignment
    eventAction: experimentName,
    eventLabel: variantAssignment,
    experimentId,
    experimentName,
    variantAssignment,
    shownVariantEntryTitle,
    timestamp,
    userSessionIdentifier,
    sourcePageSurface: pageSurface,
  });

  return exposure;
};

export type AbTestClickExposure = Pick<
  AbTestExposureContext,
  'experimentId' | 'experimentName' | 'variantAssignment'
>;

export type AbTestApplicationStartExposure = Pick<
  AbTestExposureContext,
  'experimentId' | 'experimentName' | 'variantAssignment' | 'shownVariantEntryTitle' | 'timestamp'
>;

/** BAD-2715: fires when a customer clicks A/B-tested control or variant content. */
export const trackAbTestClick = (clickTargetCtaLabel: string, exposure: AbTestClickExposure): void => {
  const timestamp = new Date().toISOString();
  const userSessionIdentifier = getUserSessionIdentifier();

  pushToDataLayer({
    event: GTM_EVENT,
    eventCategory: GTM_EVENT_CATEGORY,
    eventAction: exposure.experimentName,
    eventLabel: exposure.variantAssignment,
    experimentId: exposure.experimentId,
    experimentName: exposure.experimentName,
    variantAssignment: exposure.variantAssignment,
    clickTargetCtaLabel,
    timestamp,
    userSessionIdentifier,
  });
};

/** BAD-2715: fires when a customer starts an application (same-tab attribution via in-memory exposure). */
export const trackApplicationStart = (
  sourcePageSurface: string,
  exposureOverride?: AbTestApplicationStartExposure | null,
): void => {
  const exposure = exposureOverride ?? getStoredAbTestExposure();
  const applicationStartTimestamp = new Date().toISOString();
  const applicationSessionUserIdentifier = getUserSessionIdentifier();

  pushToDataLayer({
    event: GTM_EVENT,
    eventCategory: GTM_EVENT_CATEGORY,
    applicationStartTimestamp,
    applicationSessionUserIdentifier,
    sourcePageSurface,
    ...(exposure
      ? {
          eventAction: exposure.experimentName,
          eventLabel: exposure.variantAssignment,
          experimentId: exposure.experimentId,
          experimentName: exposure.experimentName,
          variantAssignment: exposure.variantAssignment,
          shownVariantEntryTitle: exposure.shownVariantEntryTitle,
          exposureTimestamp: exposure.timestamp,
        }
      : {}),
  });
};

export type ChatVendor = 'quiq' | 'decagon';

export type TrackChatVendorLoadInput = {
  vendor: ChatVendor;
  bucket: string | null | undefined;
  identity: 'none';
  hostname?: string;
};

/** Chat A/B (Quiq vs. Decagon): fires when a vendor's chat script loads for a bucketed customer. */
export const trackChatVendorLoad = ({ vendor, bucket, identity, hostname }: TrackChatVendorLoadInput): void => {
  pushToDataLayer({
    event: GTM_EVENT,
    eventCategory: 'ChatVendorAB',
    eventAction: 'chat-vendor-load',
    eventLabel: vendor,
    vendor,
    bucket,
    identity,
    hostname: hostname ?? (typeof window !== 'undefined' ? window.location.hostname : undefined),
    timestamp: new Date().toISOString(),
  });
};

export type ChatSuppressionReason = 'no-cookie' | 'unrecognized' | 'config-missing' | 'double-load-guard-tripped';

export type TrackChatSuppressedInput = {
  reason: ChatSuppressionReason;
  hostname?: string;
};

/** Chat A/B (Quiq vs. Decagon): fires when no vendor script is loaded — cookie absent, unrecognized, or config missing. */
export const trackChatSuppressed = ({ reason, hostname }: TrackChatSuppressedInput): void => {
  pushToDataLayer({
    event: GTM_EVENT,
    eventCategory: 'ChatVendorAB',
    eventAction: 'chat-suppressed',
    eventLabel: reason,
    reason,
    hostname: hostname ?? (typeof window !== 'undefined' ? window.location.hostname : undefined),
    timestamp: new Date().toISOString(),
  });
};

export const resolveClickTargetLabel = (element: Element): string => {
  const ariaLabel = element.getAttribute('aria-label')?.trim();
  if (ariaLabel) return ariaLabel;

  const text = element.textContent?.trim();
  if (text) return text.slice(0, 200);

  return 'unknown';
};
